import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { ErrorState } from '@/shared/ui/ErrorState';
import { Icon } from '@/shared/ui/Icon';
import { SkeletonCard } from '@/shared/ui/Skeleton';
import { MeetingTabs } from '@/features/meeting/MeetingTabs';
import { getMeeting } from '@/shared/api/meetings';
import { listScheduleItems, listScheduleComments, addScheduleComment, deleteScheduleComment } from '@/shared/api/schedule';
import { listPlaceCandidates, votePlace } from '@/shared/api/voting';
import { apiErrorMessage } from '@/shared/api/client';
import { fmtKst } from '@/shared/lib/date';
import { useAuthStore } from '@/shared/auth/store';
import { useToast } from '@/shared/ui/Toast';


export default function ScheduleItemPage() {
  const { id = '', scheduleItemId = '' } = useParams<{ id: string; scheduleItemId: string }>();
  const qc = useQueryClient();
  const toast = useToast();
  const user = useAuthStore((s) => s.user);
  const [commentText, setCommentText] = useState('');

  const meetingQ = useQuery({
    queryKey: ['meeting', id],
    queryFn: () => getMeeting(id),
    enabled: !!id,
  });
  const scheduleQ = useQuery({
    queryKey: ['schedule', id],
    queryFn: () => listScheduleItems(id),
    enabled: !!id,
  });
  const placesQ = useQuery({
    queryKey: ['places', id, scheduleItemId],
    queryFn: () => listPlaceCandidates(id, scheduleItemId),
    enabled: !!id && !!scheduleItemId,
  });
  const commentsQ = useQuery({
    queryKey: ['schedule-comments', scheduleItemId],
    queryFn: () => listScheduleComments(scheduleItemId),
    enabled: !!scheduleItemId,
  });

  const item = useMemo(
    () => (scheduleQ.data ?? []).find((row) => row.id === scheduleItemId) ?? null,
    [scheduleQ.data, scheduleItemId],
  );
  const places = placesQ.data ?? [];
  const confirmedPlace = places.find((place) => place.confirmed) ?? null;
  const maxPlaceVotes = Math.max(...places.map((place) => place.voteCount), 1);
  const isHost = meetingQ.data?.hostUserId === user?.id;

  useEffect(() => {
    if (window.location.hash !== '#schedule-comments') return;
    const handle = window.setTimeout(() => {
      document.getElementById('schedule-comments')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => window.clearTimeout(handle);
  }, [scheduleItemId, commentsQ.data?.length]);

  const votePlaceMut = useMutation({
    mutationFn: ({ placeCandidateId, yes }: { placeCandidateId: string; yes: boolean }) =>
      votePlace(placeCandidateId, yes),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['places', id, scheduleItemId] }),
    onError: (err) => toast.error(apiErrorMessage(err, '관심 표시를 저장하지 못했어요.')),
  });


  const addCommentMut = useMutation({
    mutationFn: () => addScheduleComment(scheduleItemId, commentText.trim()),
    onSuccess: () => {
      setCommentText('');
      qc.invalidateQueries({ queryKey: ['schedule-comments', scheduleItemId] });
      toast.success('댓글을 남겼어요.');
    },
    onError: (err) => toast.error(apiErrorMessage(err, '댓글을 남기지 못했어요.')),
  });

  const deleteCommentMut = useMutation({
    mutationFn: deleteScheduleComment,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedule-comments', scheduleItemId] }),
    onError: (err) => toast.error(apiErrorMessage(err, '댓글을 삭제하지 못했어요.')),
  });

  function onCommentSubmit(event: FormEvent) {
    event.preventDefault();
    if (commentText.trim().length === 0 || addCommentMut.isPending) return;
    addCommentMut.mutate();
  }

  if (scheduleQ.isLoading || meetingQ.isLoading) return <SkeletonCard />;
  if (scheduleQ.isError || meetingQ.isError) {
    return <ErrorState title="일정을 불러오지 못했어요" onRetry={() => scheduleQ.refetch()} />;
  }
  if (!item) {
    return <ErrorState title="일정을 찾지 못했어요" description="코스 화면에서 다시 선택해주세요." />;
  }

  return (
    <section className="space-y-4 bg-canvas pb-4">
      <h1 className="sr-only">일정 상세</h1>
      <MeetingTabs meetingId={id} />

      <section className="rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-tag font-semibold uppercase tracking-[0.04em] text-primary">
              {fmtKst(item.startAt, 'HH:mm')}
            </p>
            <h2 className="mt-1 text-[24px] font-semibold leading-tight tracking-[-0.7px] text-ink">
              {item.title}
            </h2>
            {item.memo ? <p className="mt-1 text-body text-ink-muted">{item.memo}</p> : null}
          </div>
          <Badge tone={confirmedPlace ? 'success' : 'primary'}>
            {confirmedPlace ? '장소 확정' : '투표 중'}
          </Badge>
        </div>
      </section>

      <section className="space-y-3" aria-labelledby="schedule-place-vote">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[15px] font-bold tracking-[-0.2px] text-ink">장소</p>
            <h2 id="schedule-place-vote" className="mt-0.5 text-[18px] font-semibold text-ink">장소투표</h2>
          </div>
          {places.length > 0 ? (
            <span className="text-small font-medium text-ink-muted">{places.length}개 후보</span>
          ) : null}
        </div>

        {placesQ.isLoading ? <div className="mt-3"><SkeletonCard /></div> : null}
        {placesQ.isError ? (
          <p className="mt-3 rounded-xl bg-error-soft px-3 py-2 text-body text-error" role="alert">
            장소 후보를 불러오지 못했어요.
          </p>
        ) : null}
        {places.length > 0 ? (
          <ul className="space-y-2" aria-label="장소 후보 목록">
            {places.map((place) => {
              const percent = Math.max(6, Math.round((place.voteCount / maxPlaceVotes) * 100));
              return (
                <li
                  key={place.id}
                  className={`relative overflow-hidden rounded-[12px] border bg-surface ${
                    place.confirmed
                      ? 'border-success/60'
                      : place.myVote
                        ? 'border-primary/50'
                        : 'border-surface-border'
                  }`}
                >
                  <div
                    aria-hidden="true"
                    className="absolute inset-y-0 left-0 bg-primary-soft"
                    style={{ width: `${percent}%` }}
                  />
                  <div className="relative z-10 flex min-h-12 items-center gap-2 px-3 py-2">
                    <p className="min-w-0 flex-1 truncate text-[14px] font-semibold text-ink">
                      {place.name}
                    </p>
                    <span className="shrink-0 text-[12px] font-semibold text-ink-muted tabular-nums">
                      {place.confirmed ? '확정' : `${place.voteCount}표`}
                    </span>
                    <button
                      type="button"
                      aria-label={place.myVote ? '투표 취소' : '투표'}
                      aria-pressed={place.myVote ?? false}
                      onClick={() => votePlaceMut.mutate({ placeCandidateId: place.id, yes: !place.myVote })}
                      className={`pressable inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-all duration-200 ease-airbnb focus-visible:outline-none focus-visible:shadow-focus-ring ${
                        place.myVote
                          ? 'bg-primary text-white'
                          : 'bg-surface/90 text-ink-muted hover:bg-primary-soft hover:text-primary'
                      }`}
                    >
                      <Icon name="check" size={15} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
        <Link
          to={`/meetings/${id}/places?from=vote&mode=map&schedule=${item.id}`}
          className="pressable flex min-h-12 items-center justify-center rounded-[12px] bg-primary text-small font-semibold text-white focus-visible:outline-none focus-visible:shadow-focus-ring"
        >
          투표 추가
        </Link>
      </section>

      <section className="rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm p-4" aria-labelledby="schedule-comments">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[15px] font-bold tracking-[-0.2px] text-ink">대화</p>
            <h2 id="schedule-comments" className="mt-0.5 text-[18px] font-semibold text-ink">댓글</h2>
          </div>
          {(commentsQ.data ?? []).length > 0 ? (
            <span className="text-small font-medium text-ink-muted">{(commentsQ.data ?? []).length}개</span>
          ) : null}
        </div>

        {commentsQ.isLoading ? (
          <p className="mt-3 rounded-2xl bg-surface-muted px-4 py-5 text-center text-body text-ink-muted">댓글을 불러오는 중…</p>
        ) : commentsQ.isError ? (
          <ErrorState title="댓글을 불러오지 못했어요" onRetry={() => commentsQ.refetch()} />
        ) : (commentsQ.data ?? []).length === 0 ? null : (
          <ul className="mt-3 space-y-2" aria-label="일정 댓글 목록">
            {(commentsQ.data ?? []).map((comment) => {
              const canDelete = comment.userId === user?.id || isHost;
              return (
                <li key={comment.id} className="rounded-2xl bg-surface-muted px-3 py-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-small font-semibold text-ink">{comment.nickname}</p>
                      <p className="mt-0.5 whitespace-pre-wrap break-words text-body leading-5 text-ink">{comment.content}</p>
                      <p className="mt-1 text-[11px] font-semibold text-ink-faint">{fmtKst(comment.createdAt, 'M월 d일 HH:mm')}</p>
                    </div>
                    {canDelete ? (
                      <button
                        type="button"
                        onClick={() => deleteCommentMut.mutate(comment.id)}
                        disabled={deleteCommentMut.isPending}
                        className="pressable inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-ink-faint hover:bg-surface-border hover:text-error focus-visible:outline-none focus-visible:shadow-focus-ring disabled:opacity-40"
                        aria-label="댓글 삭제"
                      >
                        <Icon name="x" size={16} />
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <form className="mt-3 flex items-center gap-2 rounded-[18px] border border-surface-border bg-surface-subtle p-2" onSubmit={onCommentSubmit}>
          <input
            value={commentText}
            onChange={(event) => setCommentText(event.target.value.slice(0, 300))}
            placeholder="댓글 남기기"
            aria-label="댓글 남기기"
            className="min-h-10 min-w-0 flex-1 bg-transparent px-2 text-body text-ink outline-none placeholder:text-ink-faint"
            maxLength={300}
          />
          <Button type="submit" variant="brand" disabled={commentText.trim().length === 0 || addCommentMut.isPending} className="min-h-10 shrink-0 px-3 text-[12px] text-white">
            {addCommentMut.isPending ? '작성 중' : '작성'}
          </Button>
        </form>
      </section>
    </section>
  );
}
